export const meta = {
  id: '29-the-known-world',
  order: 29,
  titleFi: 'Tunnettu maailma',
  titleEn: 'The known world',
  duolingoUnit: 'duo-34-the-known-world',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: [
    'qarth', 'junkae', 'dothraki', 'mīrīn', 'astapor', 'rios', 'qelbar', 'vurnegon', 'brāvos',
    'lȳs', 'mȳr', 'volantis', 'nāvar', 'sindilion', 'ghīs', 'īlilion', 'izulion', 'ninkion',
    'norvos', 'pentos', 'qohor', 'lentor', 'vilinion', 'zaldrījudirys-2',
    'ours:aerēbnon',
    'ours:ālīha',
    'ours:mīvarriapos',
    'ours:aerēptys',
    'ours:arrirys', 'ours:arrilion',
    'ours:uēpēgrie',
    'ours:dārāzma',
    'ours:majēbnon',
    'ours:jentelion',
    'ours:rijībnon',
    'ours:lentāzmoqitta',
    'ours:tymption',
    'ours:vokturlion',
    'ours:englandi',
    'ours:españa',
    'ours:japāni',
    'ours:kīna',
    'ours:farāsi',
    'ours:germānia',
    'ours:russia',
    'ours:vietnami',
    'ours:amerika',
    'ours:meksiko',
    'ours:sovieti',
    'ours:passi',
    'ours:pirata',
    'ours:vȳhōñe',
  ],
  exampleIds: [
    'got-s4e03-004',
    'ex-1b2ccc7ad482',
    'got-s6e05-002',
    'ex-dcf3c34e4573',
    'ex-f4d917e17275',
    'ex-bab40d5e38b8',
    'ex-97f9b5d9dc38',
    'got-s3e06-004',
    'ex-1cb9240d9061',
    'ex-cb787b09a41d',
    'ex-984efc82af22',
    'ex-f000dbb85f4e',
    'ex-5d5034973f37',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#The_known_world',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Reference_Grammar#Sixth_Declension',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Adpositions',
    'https://wiki.languageinvention.com/index.php?title=Dothraki',
    'https://wiki.languageinvention.com/index.php?title=Qarthi',
    'https://wiki.languageinvention.com/index.php?title=Junkae',
    'https://wiki.languageinvention.com/index.php?title=vurnegon',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['declensions'],
  showLines: ['got-s4e03-004', 'got-s6e05-002', 'got-s3e06-004'],
  pronunciation: { focus: 'stress', words: ['dothraki', 'Astapor', 'vurnegon', 'Volantis', 'sindilion'], lines: ['got-s4e03-004', 'got-s6e05-002', 'got-s3e06-004'] },
  letter: {
    fi: 'Kerro, missä Westerosin tai Essosin kaupungissa haluaisit käydä.',
    en: 'Write which city of Westeros or Essos you would like to visit.',
  },
  scene: 'got-s6e05-1',
}

# Tunnettu maailma

Korkeavalyria oli Valyrian vapaavallan kieli. Vapaavalta tuhoutui kauan ennen sarjojen tapahtumia, mutta kieli jäi elämään sivistyksen kielenä, vähän kuin latina meillä. Moni Essosissa puhuu sitä toisena kielenään ja pitää sitä oman valyriansa juhlavana muotona, vaikka arkikieli on ehtinyt muuttua kokonaan toiseksi kieleksi. Maailmassa puhutaan silti monia muitakin kieliä, ja tarinat kulkevat maasta toiseen.

Tällä oppitunnilla matkustetaan: Vapaakaupungit, Orjakauppiaanlahden kaupungit, joet, järvet, torit ja satamat. Ja koska vieraat paikat tuovat mukanaan vieraita sanoja, katsotaan samalla, miten valyria **lainaa**: mitä se tekee vieraille äänteille ja miten se taivuttaa vieraita sanoja.

## Kaupungit ja niiden suvut

Kaupunkien nimet ovat tavallisia substantiiveja, joilla on suku ja <Term id="deklinaatio">taivutusluokka</Term> kuten muillakin. Suvun voi usein päätellä sanan lopusta:

- <Hv>Brāvos</Hv>, <Hv>Pentos</Hv> ja <Hv>Norvos</Hv> ovat aurinkosukuisia <Hv>-os</Hv>-sanoja, samoin <Hv l="lȳs">Lȳs</Hv> ja <Hv>Ghīs</Hv>.
- <Hv>Astapor</Hv>, <Hv>Qohor</Hv> ja <Hv>Mȳr</Hv> ovat vesisukuisia.
- <Hv>Volantis</Hv> on viidennen <Term id="deklinaatio">taivutusluokan</Term> aurinkosukuinen <Hv>-is</Hv>-sana.
- <Hv>Mīrīn</Hv>, <Hv>Qarth</Hv> ja <Hv>Junkae</Hv> ovat lainasanoja, joihin palataan kohta.

Joidenkin nimien vartalo muuttuu taivutettaessa, ja ne kannattaa painaa mieleen heti: <Hv l="lȳs">Lȳs</Hv> → <Hv glossed>Lyvo</Hv> ’Lysin’, <Hv>Ghīs</Hv> → <Hv>Ghīho</Hv>, <Hv>Qohor</Hv> → <Hv>Qorho</Hv>, <Hv>Astapor</Hv> → <Hv>Astapro</Hv>. Suomessakin vartalo vaihtelee (*Lahti* → *Lahden*), joten ilmiö on sinulle tuttu, vaikka muutokset ovat toisenlaisia.

Daenerys luettelee Orjakauppiaanlahden kaupungit järjestyksessä. Katso, miten jokainen kaupunki on datiivissa, kun hän kertoo, minne meni:

<Line id="got-s4e03-004" />

<Example id="ex-1b2ccc7ad482" />

<Line id="got-s6e05-002" />

Kotipaikka ilmaistaan prepositiolla <Hv>hen</Hv> ja paikan nimellä. Nimi on lokatiivissa tai datiivissa; monissa nimissä nämä kaksi muotoa ovat samat (<Hv>Mȳrot</Hv>, <Hv>Qorhot</Hv>, <Hv>Lyvot</Hv>).

<Example id="ex-dcf3c34e4573" />

<Example id="ex-f4d917e17275" />

<Example id="ex-bab40d5e38b8" />

<Example id="ex-97f9b5d9dc38" />

Maiseman sanoilla on oma pieni perheensä: <Hv glossed>sindilion</Hv> ’tori, kauppa’, <Hv glossed>īlilion</Hv> ’risteys’, <Hv glossed>izulion</Hv> ’aukio’, <Hv glossed>ninkion</Hv> ’tasanko, pelto’ ja <Hv glossed>vilinion</Hv> ’satama’ päättyvät kaikki <Hv>-ion</Hv>:iin, ja kaikki ovat maasukuisia. Vesistöt taas ovat vesisukuisia: <Hv glossed>qelbar</Hv> ’joki’ ja <Hv glossed>nāvar</Hv> ’järvi’. <Hv glossed>Lentor</Hv> ’kylä’ on muodoltaan sanan <Hv glossed>lenton</Hv> ’talo’ kollektiivi eli ’kaikki talot yhdessä’. Kollektiivit tulivat vastaan edellisellä oppitunnilla.

<Example id="ex-984efc82af22" />

<Example id="ex-f000dbb85f4e" />

## Vieraat äänteet

Kaikki kielet lainaavat, ja lainasana joko sopeutetaan kieleen tai jätetään lähes ennalleen. Suomi on tehnyt tätä vuosisatoja, ja siksi valyrian tapa tuntuu tutulta. Ruotsin *kaffe* sai meillä *hv*:n (*kahvi*), kreikan *th* muuttui *t*:ksi (*Tuomas*, *teatteri*), ranskan *ch* eli *š* muuttui *s*:ksi (*samppanja*) ja kreikan *kh* (kirjoitetaan usein *ch*) muuttui *k*:ksi (*kemia*, *Kristus*). Valyria tekee lähes samat ratkaisut.

Lainasanoissa näet kirjainyhdistelmiä, joita varsinaisessa valyriassa ei käytetä:

- **kh** on sama äänne [x] kuin *ch* saksan sanassa *Buch*. Valyrialainen ääntää sen lähteestä riippuen *k*:na, *h*:na tai *gh*:na.
- **sh** on [ʃ] kuten suomen sanassa *šakki*. Valyriaksi se kuuluu yleensä *s*:nä, vaikka joissakin murteissa [ʃ] esiintyy yhdistelmän *sr* ääntämyksenä (ja kirjoitetaan yleensä *j*).
- **th** on [θ] kuten englannin sanassa *think*. Valyrialainen ääntää sen tavallisena *t*:nä.
- **vh**, **ph** ja **f** ovat kaikki [f]. *Vh* on vanha valyrialainen kirjoitustapa äänteelle, jota kielessä ei enää ole. Se ääntyy joko [p]:nä tai [v]:nä.

Tämän oppitunnin sanoista *th* on kahdessa: <Hv>Qarth</Hv> ja <Hv>Dothraki</Hv>. Sanakirjan ääntämysasussa niissä on vieras äänne [θ], mutta valyrialaisen suussa se kuuluu todennäköisimmin [t]:nä. *Gh* sen sijaan on valyrian oma äänne ([ɣ], kuten nimessä <Hv>Ghīs</Hv>), ei vieras.

<Pronounce word="qarth" />

<Pronounce word="dothraki" />

<Pronounce word="ghīs" />

<Line id="got-s3e06-004" />

## Vieraat taivutukset: kuudes <Term id="deklinaatio">taivutusluokka</Term>

Vieraat sanat tuovat mukanaan myös vieraita loppuja. Useimmat lainasanat kuuluvat **kuudenteen <Term id="deklinaatio">taivutusluokkaan</Term>** (sanakirjan 34 lainasanasta 22), jonka päätteet muistuttavat muutaman ja kollektiivin päätteitä. Tyypillinen lainasana päättyy perusmuodossa **-i**:hin. Suomi tekee aivan saman: konsonanttiin päättyvä vierasperäinen sana saa *i*:n, jotta se taipuisi (*bus* → *bussi*, *film* → *filmi*). Valyriassa tämä perusmuodon *-i* on vapaaehtoinen, ja jotkin sanat saavat jäädä muuttamatta: sanakirjassa on sekä <Hv l="qarthi">Qarthi</Hv> että <Hv>Qarth</Hv>. Joidenkin sanojen loppu näyttää vieraalta, kuten <Hv>Junkae</Hv>, ja niihin kannattaa kiinnittää erityistä huomiota.

Tässä on tyypillisen *i*-loppuisen lainasanan koko taivutus:

<Paradigm lemma="dothraki" numbers="sg,pl" />

<Compare fi="dothraki|n" hv="Dothrak|o" label="genetiivi ~ genetiivi" />

<Compare fi="Meereeni|ssä" hv="Mīrīn|ī" label="inessiivi ~ lokatiivi" />

<Example id="ex-1cb9240d9061" />

<Interlinear hv="Daenerys ampa pyr|ī Dothrak|oti ē|za." gloss="Daenerys-NOM kymmenen tuhat-ACC.PL dothraki-GEN.PL omistaa-PRS.3SG" fi="Daenerysilla on kymmenentuhatta dothrakia." />

<Example id="ex-cb787b09a41d" />

Kaikki lainasanat eivät taivu juuri näin, mutta Duolingon Tips & Notes antaa tuntomerkit, joiden avulla sijan tunnistaa vieraastakin sanasta:

- **s** instrumentaalissa (<Hv>Dothrakisi</Hv>),
- **m** komitatiivissa (<Hv>Dothrakimi</Hv>),
- **pitkä vokaali** lokatiivissa (<Hv>Mīrīnī</Hv>) – tai lokatiivi on sama kuin datiivi,
- **jonkinlainen -i** monikossa ja akkusatiivissa,
- **lisätty (epenteettinen) h** joissakin muodoissa: <Hv>Junkae</Hv> → <Hv>Junkaehot</Hv>.

Viimeisen näet Daenerysin luettelossa. Sarjan repliikissä kaupungin nimi kirjoitetaan *Yunkai* ja datiivi <Hv>Yunkaihot</Hv>, sanakirjassa nimi on <Hv>Junkae</Hv>; *h* on sama.

<Note>
<Hv glossed>Vurnegon</Hv> ’tarkastella, katsastaa’ on tämän oppitunnin ainoa verbi. Sen objekti on datiivissa – tai genetiivissä, jos se on yksikössä ja seisoo heti verbin edellä. Verbin taustalla on erityinen etuliite, josta kerrotaan <LessonLink n={33}>oppitunnilla 33</LessonLink>. Siihen asti riittää, että muistat sijasäännön.

<Example id="ex-5d5034973f37" />

<Interlinear hv="Dāria rih|o vūnd|as." gloss="kuningatar-NOM laakso-GEN tarkastella-PRF.3SG" fi="Kuningatar tarkasteli laaksoa." />
</Note>

## Harjoituslauseita

<Practice hv="Qarthot istan." fi="Menin Qarthiin." />

<Practice hv="Dothrakī ūndan." fi="Näin dothrakit." />

<Practice hv="Ābra hen Lyvot issa." fi="Nainen on kotoisin Lysistä." />

## Muistettavaksi

- Kaupunkien nimet ovat tavallisia substantiiveja: niillä on suku ja <Term id="deklinaatio">taivutusluokka</Term>, joskus myös vartalonvaihtelu (<Hv l="lȳs">Lȳs</Hv> → <Hv>Lyvo</Hv>, <Hv>Qohor</Hv> → <Hv>Qorho</Hv>).
- Vieraat äänteet kh, sh, th ja f sopeutuvat valyrian omiin äänteisiin: k/h/gh, s, t, p/v.
- Useimmat lainasanat kuuluvat kuudenteen <Term id="deklinaatio">taivutusluokkaan</Term> (sanakirjan 34 lainasanasta 22); perusmuodon *-i* on vapaaehtoinen.
- Tuntomerkit: s instrumentaalissa, m komitatiivissa, pitkä vokaali lokatiivissa, i monikossa ja akkusatiivissa, joskus lisätty h.

<Vocab />

<Check />
